import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";

import ErrorPage from "@/views/ErrorPage.vue";
import LogIn from "@/views/LogIn.vue";
import Verify from "@/views/Verify.vue";
import InvoicingPage from "@/views/invoicing/InvoicingPage.vue";
import OpenedCreditNotes from "@/views/invoicing/OpenedCreditNotes.vue";
import OfferedAmountsToValidate from "@/views/invoicing/OfferedAmountsToValidate.vue";

import { clientRoutes } from "./clientRoutes";
import { ProjectRoutes, projectRoutes } from "./projectRoutes";
import { adminRoutes, AdminRoutes } from "./adminRoutes";

import {
  getUser,
  hasRole,
  isAdmin,
  isCFO,
  isEmployee,
  isFinancial,
  isSuperAdmin,
} from "@/helpers/router/queries";
import IndependentInvoices from "@/views/invoicing/IndependentInvoices.vue";
import TimesheetTimerPage from "@/views/TimesheetTimerPage.vue";
import EmployeeHome from "@/views/EmployeeHome.vue";
import EmployeeProjectDetails from "@/views/EmployeeProjectDetails.vue";
import ClientInvoices from "@/views/client/ClientInvoices.vue";
import OpenedInvoices from "@/views/invoicing/OpenedInvoices.vue";
import RevenueForecast from "@/views/RevenueForecast.vue";
import AdminCfoDashboard from "@/views/admin/AdminCfoDashboard.vue";
import AdminAudit from "@/views/admin/AdminAudit.vue";
import orchaRoutes from "@/router/orchaRoutes";
import ProjectToInvoice from "@/views/invoicing/ProjectsToInvoice.vue";
import Planning from "@/views/planning/Planning.vue";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/employee",
    name: "EmployeeHome",
    component: EmployeeHome,
    meta: { requiresAuth: true, requiresEmployee: true },
  },
  {
    path: "/employee/project/:id",
    name: "EmployeeProjectDetails",
    component: EmployeeProjectDetails,
    meta: { requiresAuth: true, requiresEmployee: true },
  },
  {
    path: "/timers",
    name: "TimesheetTimer",
    component: TimesheetTimerPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/invoices",
    name: "ClientInvoices",
    component: ClientInvoices,
    meta: { requiresAuth: true },
  },
  {
    path: "/invoicing",
    component: InvoicingPage,
    name: "InvoicingRoot",
    meta: {
      requiresAuth: true,
      requiresAny: ["admin", "super_admin", "financial"],
    },
    props: true,
    beforeEnter: async (route) => {
      if (route.name === "InvoicingRoot") {
        const user = await getUser();
        if (isFinancial(user.accessRights)) return { name: "OpenedInvoices" };
        return { name: "ProjectToInvoice" };
      }
    },
    children: [
      {
        path: "projects",
        name: "ProjectToInvoice",
        component: ProjectToInvoice,
        meta: { requiresAuth: true, requiresAdmin: true },
        props: true,
      },
      {
        path: "independent",
        name: "IndependentInvoices",
        component: IndependentInvoices,
        meta: { requiresAuth: true, requiresAdmin: true },
        props: true,
      },
      {
        path: "opened-credit-notes",
        name: "OpenedCreditNotes",
        component: OpenedCreditNotes,
        meta: { requiresAuth: true, requiresAdmin: true },
        props: true,
      },
      {
        path: "opened-invoices",
        name: "OpenedInvoices",
        component: OpenedInvoices,
        props: true,
      },
      {
        path: "offered-amounts-to-validate",
        name: "OfferedAmountsToValidate",
        component: OfferedAmountsToValidate,
        props: true,
      },
    ],
  },
  {
    path: "/revenue",
    name: "RevenueForecast",
    component: RevenueForecast,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/cfo-dashboard",
    name: "AdminCfoDashboard",
    component: AdminCfoDashboard,
    meta: { requiresAuth: true, requiresCFO: true },
  },
  {
    // Top-level, menu-less entry point for the Super Admin Audit page.
    // Accessible only via direct URL by super admins. It is intentionally
    // not listed in any tab/menu so it stays "hidden" in the navigation,
    // while still being reachable (and bookmarkable) through /audit.
    path: "/audit",
    name: AdminRoutes.ADMIN_AUDIT,
    component: AdminAudit,
    // The Orcha subgraph already gates the query behind admin
    // permission, and the Fides subgraph gates it behind super admin.
    // We require super-admin here so a regular Fides admin (who may not
    // have orcha admin rights) cannot trigger a remote "Not Authorised!"
    // from the Orcha subgraph.
    meta: { requiresAuth: true, requiresSuperAdmin: true },
  },
  {
    // Admin-only page that issues a short-lived one-time reconnect
    // token for the My26 LinkedIn Bridge Chrome extension.
    // The actual session POST authenticates via the token, not via
    // the My26 browser session, so the admin does not need to be
    // logged into My26 inside the extension's browser.
    path: "/reconnect-linkedin-session",
    name: "ReconnectLinkedinSession",
    component: () =>
      import(
        /* webpackChunkName: "reconnect-linkedin-session" */ "@/views/admin/AdminReconnectLinkedinSession.vue"
      ),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/my-planning",
    name: "My Planning",
    component: Planning,
    meta: { requiresAuth: true },
  },
  {
    path: "/",
    name: "Home",
    component: ErrorPage,
    beforeEnter: async () => {
      const user = await getUser();
      if (user) {
        if (isEmployee(user.accessRights)) return { name: "EmployeeHome" };
        if (isFinancial(user.accessRights)) return { name: "OpenedInvoices" };
        return { name: ProjectRoutes.PROJECTS_ACTIVE };
      }
      return { name: "Login" };
    },
  },
  {
    path: "/login",
    name: "Login",
    component: LogIn,
    beforeEnter: async () => {
      const user = await getUser();
      if (user) return { name: "Home" };
    },
  },
  {
    path: "/auth/confirm",
    name: "Verify",
    component: Verify,
    props: (route) => ({ email: route.query.email, token: route.query.token }),
    beforeEnter: async (route) => {
      const user = await getUser();
      if (user) {
        if (route.query.redirect) return { path: route.query.redirect };
        return { name: "Home" };
      }
    },
  },
  // Signed action routes - public, no authentication required
  {
    path: "/signed/order/:token",
    name: "SignedOrderAction",
    component: () =>
      import(
        /* webpackChunkName: "signed-order-action" */ "@/views/SignedOrderAction.vue"
      ),
    props: (route) => ({ token: route.params.token }),
  },
  {
    path: "/signed/recurring-order/:token",
    name: "SignedRecurringOrderAction",
    component: () =>
      import(
        /* webpackChunkName: "signed-recurring-order-action" */ "@/views/SignedRecurringOrderAction.vue"
      ),
    props: (route) => ({ token: route.params.token }),
  },
];

routes.push(...clientRoutes, ...projectRoutes, ...orchaRoutes, ...adminRoutes);

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from) => {
  if (to.meta.requiresAuth) {
    const user = await getUser();
    if (!user) {
      return {
        path: "/login",
        query: { redirect: to.fullPath },
      };
    }
    if (to.meta.requiresAny) {
      if (
        !(to.meta.requiresAny as string[]).some((role) =>
          hasRole(role, user.accessRights),
        )
      ) {
        return {
          path: from.fullPath,
        };
      }
    }
    if (to.meta.requiresAdmin) {
      if (!isAdmin(user.accessRights)) {
        return {
          path: from.fullPath,
        };
      }
    }
    if (to.meta.requiresCFO) {
      if (!isCFO(user.accessRights)) {
        return {
          path: from.fullPath,
        };
      }
    }
    if (to.meta.requiresSuperAdmin) {
      if (!isSuperAdmin(user.accessRights)) {
        return {
          path: from.fullPath,
        };
      }
    }
    if (to.meta.requiresEmployee) {
      if (!isEmployee(user.accessRights)) {
        return {
          path: from.fullPath,
        };
      }
    }
  }
});

export default router;
